import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { TrackedLink } from "@/components/TrackedLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GoogleIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

let cache = null;
export const useReviews = () => {
  const [data, setData] = useState(cache);
  useEffect(() => {
    if (cache) return;
    axios.get(`${API}/reviews`).then((r) => { cache = r.data; setData(r.data); }).catch(() => setData({ rating: null, reviews: [] }));
  }, []);
  return data;
};

export const Stars = ({ n = 5, size = 16 }) => (
  <span className="inline-flex" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => <Star key={star} size={size} className={star <= n ? "fill-yellow-400 text-yellow-400" : "text-hacienda-border-dark"} aria-hidden="true" />)}
  </span>
);

export const RatingBadge = ({ compact = false }) => {
  const data = useReviews();
  const { t } = useLang();
  if (!data?.rating) return null;
  return (
    <a href={data.google_reviews_url || SITE.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="google-rating-badge">
      <GoogleIcon size={compact ? 18 : 22} />
      <span className="font-semibold text-hacienda-charcoal">{data.rating.toFixed(1)}</span>
      <Stars n={Math.round(data.rating)} size={compact ? 14 : 16} />
      <span className="text-hacienda-gray text-sm" data-testid="google-review-count">
        {data.review_count ? `${data.review_count} ${t("info.reviews")}` : t("info.onGoogle")}
      </span>
    </a>
  );
};

export const ReviewsSection = ({ limit = 3, className = "" }) => {
  const data = useReviews();
  const { t } = useLang();
  const reviews = (data?.reviews || []).slice(0, limit);
  return (
    <section className={`py-24 px-4 ${className}`} data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="md:flex md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <GoogleIcon size={26} />
              <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em]">{t("reviews.eyebrow")}</p>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-hacienda-charcoal font-semibold">{t("reviews.title")}</h2>
          </div>
          <div className="mt-6 md:mt-0 flex flex-col items-start md:items-end gap-3">
            <RatingBadge />
            <TrackedLink href={data?.write_review_url || SITE.googleReviewsUrl} event="review_click" variant="outline" size="sm" data-testid="leave-review-btn">
              <GoogleIcon size={16} /> {t("actions.leaveReview")}
            </TrackedLink>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <a
              key={`${r.author}-${r.date}`}
              href={r.url || SITE.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-hacienda-cream p-7 rounded-2xl border border-hacienda-border-light hover:border-hacienda-terracotta/50 transition-colors flex flex-col"
              data-testid={`review-card-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <Stars n={r.rating} />
                <ExternalLink size={14} className="text-hacienda-gray opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
              </div>
              <p className="text-hacienda-gray leading-relaxed flex-grow">“{r.text}”</p>
              <div className="mt-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {r.author_photo && <img src={r.author_photo} alt="" width="32" height="32" loading="lazy" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full flex-shrink-0" />}
                  <p className="font-semibold text-hacienda-charcoal truncate">{r.author}</p>
                </div>
                <p className="text-hacienda-gray text-sm whitespace-nowrap">{r.date}</p>
              </div>
            </a>
          ))}
        </div>

        <p className="text-center text-hacienda-gray text-xs mt-8" data-testid="reviews-source-note">
          {data?.source === "google" ? t("reviews.order") : t("reviews.verifiedNote")}
        </p>
        <div className="text-center mt-3">
          <a href={data?.google_reviews_url || SITE.googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-hacienda-terracotta hover:underline underline-offset-4 font-medium" data-testid="view-all-reviews-link">
            {t("actions.viewAll")} <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};
